import { useCallback } from 'react';
import { parseTimestampToSeconds } from '@/shared/lib/utils/timestamp';
import { useYoutubePlayerStore } from '@/shared/store/youtube-player';
import type { TimelineBlock as TimelineBlockType } from '@/shared/types/blocks';

type TimelineBlockProps = Omit<TimelineBlockType, 'type' | 'id'> & {
  /** 연결된 YouTube 영상 ID */
  linkedVideoId?: string;
};

/**
 * TimelineBlock - 타임라인
 *
 * 시간순 이벤트를 타임라인 형태로 표시합니다.
 * YouTube 영상 연동 시 타임스탬프 클릭으로 해당 시점으로 이동합니다.
 */
export function TimelineBlock({ items, linkedVideoId }: TimelineBlockProps) {
  const { requestSeek } = useYoutubePlayerStore();

  const handleTimestampClick = useCallback(
    (timestamp: string) => {
      if (!linkedVideoId) return;
      const seconds = parseTimestampToSeconds(timestamp);
      requestSeek(linkedVideoId, seconds);
    },
    [linkedVideoId, requestSeek],
  );

  return (
    <div className="relative">
      {/* 세로 라인 */}
      <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

      <div className="flex flex-col gap-6">
        {items.map((item) => (
          <div key={item.id} className="relative flex gap-4">
            {/* 타임스탬프 도트 */}
            <div className="relative z-10 flex items-center justify-center w-8 h-8 bg-white border-2 border-primary rounded-full shrink-0">
              <div className="w-2 h-2 bg-primary rounded-full" />
            </div>

            {/* 콘텐츠 */}
            <div className="flex-1 pt-1">
              <div className="flex items-baseline gap-2 mb-1">
                {/* 타임스탬프: 클릭 가능하게 변경 */}
                {linkedVideoId ? (
                  <button
                    type="button"
                    onClick={() => handleTimestampClick(item.timestamp)}
                    className="text-xs font-medium text-primary hover:text-primary/80 hover:underline cursor-pointer transition-colors"
                  >
                    {item.timestamp}
                  </button>
                ) : (
                  <span className="text-xs font-medium text-muted-foreground">
                    {item.timestamp}
                  </span>
                )}
                <h4 className="text-base font-bold text-foreground">{item.title}</h4>
              </div>

              {item.description && (
                <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
              )}

              {item.bulletPoints && item.bulletPoints.length > 0 && (
                <ul className="list-disc list-inside space-y-1">
                  {item.bulletPoints.map((point, index) => (
                    <li key={`${item.id}-point-${index}`} className="text-sm text-muted-foreground">
                      {point}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
